import React, { useState, useEffect } from 'react';
import {
    Box,
    CircularProgress,
    Alert,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    useTheme,
    useMediaQuery,
    styled,
    Paper,
    Grid,
    Skeleton,
    Chip,
    Divider,
    Card,
    CardContent,
    Avatar
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useYoklamaSistemi } from '../../../hooks/useYoklamaSistemi';
import { useAuth } from '../../../contexts/AuthContext';
import { YoklamaSistemiError } from '../../../types/ErrorModals/YoklamaSistemiError';
import { DersKart } from '../../../components/YoklamaSistemi/DersKart';
import { getYoklamaSistemi } from '../../../services/api/yoklamaSistemi';
import ClassIcon from '@mui/icons-material/Class';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SchoolIcon from '@mui/icons-material/School';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { getCurrentSezon, getSezonOptions } from '../../../utils/sezon';

// Styled components
const SeasonSelector = styled(FormControl)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius * 1.5,
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StatsCard = styled(Card)(({ theme }) => ({
    height: '100%',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: theme.shadows[6],
    },
}));

const YoklamaSistemi: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { kullaniciModal } = useAuth();
    
    const [selectedSeason, setSelectedSeason] = useState<string>(String(getCurrentSezon()));
    const availableSeasons = getSezonOptions().map(String);
    
    const handleSeasonChange = (event: SelectChangeEvent) => {
        setSelectedSeason(event.target.value);
    };
    
    const {
        data,
        loading,
        error,
        selectedDurum,
        expandedCards,
        kayitYapiliyor,
        handleDurumChange,
        toggleCard,
        handleYoklamaAl,
        refetch
    } = useYoklamaSistemi(
        localStorage.getItem('kurumKodu') || '',
        Number(kullaniciModal?.id) || 0,
        parseInt(selectedSeason)
    );
    
    useEffect(() => {
        if (kullaniciModal?.id) {
            refetch();
        }
    }, [selectedSeason, refetch, kullaniciModal?.id]);
    
    // Calculate statistics
    const getStatistics = () => {
        if (!data || data.length === 0) return { dersCount: 0, studentCount: 0, markedCount: 0 };
        
        const studentCount = data.reduce((acc, ders) => acc + ders.ogrenciDurumListesi.length, 0);
        const markedCount = Object.values(selectedDurum).reduce((acc, dersDurumlari) => {
            return acc + Object.keys(dersDurumlari).length;
        }, 0);
        
        return {
            dersCount: data.length,
            studentCount,
            markedCount
        };
    };
    
    const stats = getStatistics();
    
    // Render skeletons for better UX during loading
    const renderSkeletons = () => {
        return Array(3).fill(0).map((_, index) => (
            <Paper key={index} sx={{ mb: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 2, backgroundColor: theme.palette.primary.main }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={{ p: 2 }}>
                    {Array(3).fill(0).map((_, i) => (
                        <Box key={i} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <Skeleton variant="text" />
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <Skeleton variant="rectangular" height={32} />
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <Skeleton variant="rectangular" height={32} />
                                </Grid>
                            </Grid>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                    <Grid container justifyContent="space-between">
                        <Grid item xs={6}>
                            <Skeleton variant="text" width="80%" />
                        </Grid>
                        <Grid item xs={4}>
                            <Skeleton variant="rectangular" height={36} />
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        ));
    };
    
    return (
        <Box sx={{ px: { xs: 1, sm: 2 } }}>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 3
            }}>
                <Typography 
                    variant="h5" 
                    component="h1"
                    sx={{ 
                        fontWeight: 600, 
                        mb: { xs: 2, sm: 0 },
                        color: theme.palette.text.primary
                    }}
                >
                    <HowToRegIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Yoklama Sistemi
                </Typography>
                
                <SeasonSelector
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 200 }}
                >
                    <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
                    <Select
                        labelId="season-select-label"
                        id="season-select"
                        value={selectedSeason}
                        onChange={handleSeasonChange}
                        label="Eğitim Sezonu"
                    >
                        {availableSeasons.map((season) => (
                            <MenuItem key={season} value={season}>
                                {parseInt(season) - 1} - {season} Eğitim Yılı
                            </MenuItem>
                        ))}
                    </Select>
                </SeasonSelector>
            </Box>
            
            {/* Statistics Cards */}
            {!loading && !error && data?.length > 0 && (
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <StatsCard elevation={3}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                    <ClassIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{stats.dersCount}</Typography>
                                    <Typography variant="body2" color="text.secondary">Toplam Ders</Typography>
                                </Box>
                            </CardContent>
                        </StatsCard>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                        <StatsCard elevation={3}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                                    <SchoolIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{stats.studentCount}</Typography>
                                    <Typography variant="body2" color="text.secondary">Toplam Öğrenci</Typography>
                                </Box>
                            </CardContent>
                        </StatsCard>
                    </Grid>
                    
                    <Grid item xs={12} sm={4}>
                        <StatsCard elevation={3}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                                    <HowToRegIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">{stats.markedCount}</Typography>
                                    <Typography variant="body2" color="text.secondary">İşaretlenen Yoklama</Typography>
                                </Box>
                            </CardContent>
                        </StatsCard>
                    </Grid>
                </Grid>
            )}
            
            {loading ? (
                <Box sx={{ mt: 3 }}>
                    {renderSkeletons()}
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    variant="filled"
                    sx={{ 
                        mt: 2, 
                        borderRadius: theme.shape.borderRadius * 1.5,
                        boxShadow: theme.shadows[3]
                    }}
                    action={
                        process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block" sx={{ color: 'white' }}>
                                CorrelationId: {error.correlationId}
                            </Typography>
                        )
                    }
                >
                    {error.message}
                </Alert>
            ) : !data?.length ? (
                <Alert 
                    severity="info" 
                    variant="outlined"
                    icon={<CalendarTodayIcon />}
                    sx={{ 
                        mt: 2, 
                        borderRadius: theme.shape.borderRadius * 1.5,
                        boxShadow: theme.shadows[1],
                        bgcolor: theme.palette.background.paper
                    }}
                >
                    <Typography variant="body1">
                        {parseInt(selectedSeason) - 1} - {selectedSeason} eğitim sezonunda yoklama alınacak ders bulunmamaktadır.
                    </Typography>
                </Alert>
            ) : (
                <Box sx={{ mt: 2 }}>
                    {data.map((ders) => (
                        <DersKart
                            key={ders.id}
                            ders={ders}
                            isExpanded={expandedCards[ders.id]}
                            selectedDurum={selectedDurum}
                            kayitYapiliyor={kayitYapiliyor}
                            onToggle={() => toggleCard(ders.id)}
                            onDurumChange={handleDurumChange}
                            onYoklamaAl={() => kullaniciModal && handleYoklamaAl(
                                ders.id, 
                                ders.ogretmenId, 
                                ders.sinifId, 
                                localStorage.getItem('kurumKodu') || '', 
                                ders.saatNo,
                                parseInt(selectedSeason)
                            )}
                            isYoklamaEnabled={true}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default YoklamaSistemi;